import React, { useState } from "react"
import {
  Box,
  Button,
  createTheme,
  IconButton,
  InputAdornment,
  TextField,
  ThemeProvider,
  Typography,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import { updateUserName } from "../../../api/user"
import { useAppSelector } from "../../../store"

// Стилизация темы под темный милитари-интерфейс игры
const gameTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#c29d53", // Золотисто-бежевый оттенок кнопок и рамок
    },
    background: {
      default: "#0a0a0a",
      paper: "#0d0d0c", // Цвет фона карточки
    },
    text: {
      primary: "#ffffff",
      secondary: "#a1a1aa",
    },
  },
  typography: {
    fontFamily: '"Roboto Condensed", "Arial", sans-serif',
  },
})

export default function EditUsernameDialog({
  open = true,
  onClose,
  onSave,
  disableClose = false,
  initialUsername = "",
}) {
  const { user } = useAppSelector((state) => state.user)
  const [username, setUsername] = useState(initialUsername)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")
  const minLen = 3
  const maxLen = 15

  const handleChange = (e) => {
    const val = e.target.value
    // Разрешаем ввод только букв, цифр и нижнего подчеркивания согласно правилу
    if (/^[a-zA-Z0-9_]*$/.test(val) && val.length <= maxLen) {
      setUsername(val)
      setError("")
    }
  }

  // Проверка условий валидации
  const isLengthValid = username.length >= minLen && username.length <= maxLen
  const isCharsValid = /^[a-zA-Z0-9_]+$/.test(username) || username.length === 0
  const canSave = isLengthValid && isCharsValid && !saving

  const handleSave = async () => {
    if (!canSave) {
      return
    }

    try {
      setSaving(true)
      setError("")

      const data = await updateUserName(user.sessionToken, username)

      if (data?.success) {
        await onSave(data.username || username)
        return
      }

      setError(data?.error || "Failed to update username")
    } catch (error) {
      setError(error?.message || "Failed to update username")
    } finally {
      setSaving(false)
    }
  }

  return (
    <ThemeProvider theme={gameTheme}>
      {/* Центрирующий контейнер-оверлей */}
      <Box
        sx={{
          display: open ? "flex" : "none",
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          bgcolor: "rgba(0,0,0,0.8)",
          zIndex: 1300,
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        {/* Само модальное окно */}
        <Box
          sx={{
            width: "100%",
            maxWidth: 440,
            bgcolor: "background.paper",
            border: "1px solid #c29d53",
            borderRadius: "8px",
            p: 4,
            position: "relative",
            boxShadow: "0px 10px 30px rgba(0,0,0,0.7)",
          }}
        >
          {!disableClose && (
            <IconButton
              onClick={onClose}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                color: "#a1a1aa",
                "&:hover": { color: "#ffffff" },
              }}
            >
              <CloseIcon />
            </IconButton>
          )}

          {/* Заголовок */}
          <Typography
            variant="h5"
            component="h2"
            sx={{
              textAlign: "center",
              fontWeight: 900,
              letterSpacing: "0.07em",
              color: "#c29d53",
              textTransform: "uppercase",
              mb: 1,
              mt: 1,
            }}
          >
            Edit Username
          </Typography>

          <Typography
            variant="body2"
            sx={{
              textAlign: "center",
              color: "text.secondary",
              mb: 4,
              fontSize: "14px",
            }}
          >
            Choose your public username.
          </Typography>

          {/* Лейбл инпута */}
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "#c29d53",
              fontWeight: "bold",
              letterSpacing: "0.05em",
              mb: 1,
              textTransform: "uppercase",
              fontSize: "11px",
            }}
          >
            Username
          </Typography>

          {/* Поле ввода */}
          <TextField
            fullWidth
            variant="outlined"
            value={username}
            onChange={handleChange}
            autoComplete="off"
            autoFocus
            error={!!error}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography
                    variant="caption"
                    sx={{ color: "#52525b", fontWeight: "bold" }}
                  >
                    {username.length} / {maxLen}
                  </Typography>
                </InputAdornment>
              ),
            }}
            sx={{
              mb: error ? 1 : 3,
              "& .MuiOutlinedInput-root": {
                color: "#ffffff",
                backgroundColor: "rgba(255,255,255,0.02)",
                "& fieldset": {
                  borderColor: "#27272a",
                },
                "&:hover fieldset": {
                  borderColor: "#3f3f46",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#c29d53",
                },
              },
              "& .MuiOutlinedInput-input": {
                py: "14px",
                px: "16px",
              },
            }}
          />

          {error && (
            <Typography
              variant="body2"
              sx={{
                color: "#ef4444",
                fontSize: "13px",
                mb: 3,
              }}
            >
              {error}
            </Typography>
          )}

          {/* Список правил валидации */}
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 4 }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <CheckCircleOutlineIcon
                sx={{
                  fontSize: "18px",
                  color: isLengthValid ? "#22c55e" : "#52525b",
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: isLengthValid ? "#a1a1aa" : "#52525b",
                  fontSize: "13px",
                }}
              >
                {minLen}-{maxLen} characters
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <CheckCircleOutlineIcon
                sx={{
                  fontSize: "18px",
                  color:
                    isCharsValid && username.length > 0 ? "#22c55e" : "#52525b",
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color:
                    isCharsValid && username.length > 0 ? "#a1a1aa" : "#52525b",
                  fontSize: "13px",
                }}
              >
                Letters, numbers and _ only
              </Typography>
            </Box>
          </Box>

          {/* Блок кнопок управления */}
          <Box sx={{ display: "flex", gap: 2 }}>
            {!disableClose && (
              <Button
                fullWidth
                variant="outlined"
                onClick={onClose}
                sx={{
                  py: 1.5,
                  borderColor: "#27272a",
                  color: "#ffffff",
                  fontWeight: "bold",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  "&:hover": {
                    borderColor: "#3f3f46",
                    backgroundColor: "rgba(255,255,255,0.02)",
                  },
                }}
              >
                Cancel
              </Button>
            )}

            <Button
              fullWidth
              variant="contained"
              disableElevation
              disabled={!canSave}
              onClick={handleSave}
              sx={{
                py: 1.5,
                bgcolor: "#c29d53",
                color: "#000000",
                fontWeight: "900",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                "&:hover": {
                  bgcolor: "#b08c44",
                },
                "&.Mui-disabled": {
                  bgcolor: "rgba(194, 157, 83, 0.3)",
                  color: "rgba(0,0,0,0.5)",
                },
              }}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  )
}
